// Which columns the Jobs table shows — spec: owner request, 2026-08-05.
//
// One customer runs their business off custom form fields ("Job Description", "Technician Notes") that the table
// could not show at all, so the list told her less than the job did. The ask was to let people pick
// their own columns WITHOUT changing anything for those who are happy: "so that I don't have to
// make this for everyone who are happy to see the default columns".
//
// So the default is exactly the five columns the table has always had, byte for byte, and a saved
// layout only exists once someone deliberately changes it. No layout in storage means default.
//
// Stored per user, in localStorage. It is a view preference on one machine, not company policy:
// two people sharing an office should not fight over each other's columns, and the owner asked for
// it to live on the local machine.

export interface JobColumn {
  key: string
  label: string
  /** CSS grid track for this column. */
  width: string
  align?: 'right'
  /** Custom form fields are `cf:<field id>` and read out of task_field_value. */
  fieldId?: string
}

/** The built-in columns, in the order they are offered. `name` is not here — see BASE_COLUMN. */
export const BUILTIN_COLUMNS: JobColumn[] = [
  { key: 'client', label: 'Client', width: '150px' },
  { key: 'assigned', label: 'Assigned', width: '140px' },
  { key: 'scheduled', label: 'Scheduled', width: '170px' },
  { key: 'finishes', label: 'Finishes', width: '170px' },
  { key: 'address', label: 'Address', width: '1fr' },
  { key: 'number', label: 'Job #', width: '90px' },
  { key: 'created', label: 'Created', width: '150px' },
  { key: 'status', label: 'Status', width: '110px', align: 'right' },
]

/**
 * The job name is always the first column and cannot be removed: it carries the link to the job,
 * and a row you cannot click is not a row. It is also where the address falls back to when the
 * Address column is not shown, which is the behaviour the table has always had.
 */
export const BASE_COLUMN: JobColumn = { key: 'name', label: 'Job', width: '1.4fr' }

/** Exactly what the table showed before any of this existed. Changing this changes everyone. */
export const DEFAULT_KEYS = ['client', 'assigned', 'scheduled', 'status']

const KEY_PREFIX = 'sp.jobs.columns.v1'
const storageKey = (userId: string | null | undefined) => `${KEY_PREFIX}:${userId ?? 'anon'}`

/**
 * The saved key list, or null when the user has never chosen — null and "same as default" are
 * deliberately different: null means "follow the default forever", including if we change it later.
 */
export function loadColumnKeys(userId: string | null | undefined): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.some((k) => typeof k !== 'string')) return null
    return parsed as string[]
  } catch {
    // Private browsing, a full quota, or someone's hand-edited JSON. A broken preference must not
    // take the Jobs page down with it — fall back to the default.
    return null
  }
}

export function saveColumnKeys(userId: string | null | undefined, keys: string[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(keys))
  } catch {
    /* Not worth interrupting anyone over: the table still works, it just won't remember. */
  }
}

export function clearColumnKeys(userId: string | null | undefined): void {
  try {
    localStorage.removeItem(storageKey(userId))
  } catch { /* see above */ }
}

/**
 * Resolve saved keys against what is actually available now.
 *
 * Custom fields get deleted and forms get edited, so a saved layout can name a column that no
 * longer exists. Those are dropped rather than rendered as blanks — but only from the RESULT, never
 * from storage, so a field that comes back (or a form still loading) does not cost someone the
 * column they chose.
 */
export function resolveColumns(keys: string[] | null, customFields: JobColumn[]): JobColumn[] {
  const available = new Map<string, JobColumn>()
  for (const c of BUILTIN_COLUMNS) available.set(c.key, c)
  for (const c of customFields) available.set(c.key, c)

  const wanted = keys ?? DEFAULT_KEYS
  const picked = wanted.map((k) => available.get(k)).filter((c): c is JobColumn => !!c)

  // Status always sits last. It is right-aligned and terminal — a coloured pill floating between
  // two text columns reads as a mistake, and turning any column on used to push it into the middle.
  const status = picked.filter((c) => c.key === 'status')
  return [...picked.filter((c) => c.key !== 'status'), ...status]
}

/** The grid template for the whole row, including the fixed job column. */
export function gridTemplate(columns: JobColumn[]): string {
  return [BASE_COLUMN.width, ...columns.map((c) => c.width)].join(' ')
}
